import * as Sentry from "@sentry/react";
import { useEffect, useMemo, useRef } from "react";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import type { Editor } from "@tiptap/react";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { useSearchableRegistry, type Searchable, type SearchState } from "./SearchableContext";
import { searchTiptapDoc, collectTiptapMatchPositions, extractTextSegments, searchTextSegments, findScrollContainer, scrollToCenterElement, type SearchResult, type TextSegment } from "./utils";

/**
 * Creates decorations for search matches in a TipTap document
 */
function createSearchDecorations(
  doc: ProseMirrorNode,
  searchTerm: string,
  selectedResult: SearchResult | null,
  fieldId: string
): DecorationSet {
  if (!searchTerm) return DecorationSet.empty;

  const matches = searchTiptapDoc(doc, searchTerm, fieldId, "");
  if (!matches.length) return DecorationSet.empty;

  const decorations = matches.map((match) => {
    const isSelected =
      selectedResult &&
      selectedResult.sourceId === fieldId &&
      selectedResult.from === match.from &&
      selectedResult.to === match.to;

    return Decoration.inline(match.from, match.to, {
      class: isSelected ? "search-highlight search-highlight-selected" : "search-highlight",
    });
  });

  return DecorationSet.create(doc, decorations);
}

export interface TiptapFieldSearchableOptions {
  /** Unique identifier for this searchable */
  id?: string;
  /** Display name (e.g., "Character: John - Backstory") */
  name?: string;
}

/**
 * Makes a TipTap field (from useEditor) searchable within the global find/replace system.
 * This is for small TipTap editors like those in Characters/Worldbuilding/Outline.
 *
 * PERF: Uses useSearchableRegistry() instead of useSearchableContext() to avoid
 * re-rendering 1000+ field components when searchState changes. Search state
 * changes are handled imperatively via subscribeToSearchState, and ProseMirror
 * decoration plugins are created lazily only when a search is active.
 *
 * @param editor - The TipTap editor instance from useEditor()
 * @param options - Configuration options
 */
export function useTiptapFieldSearchable(
  editor: Editor | null,
  options?: TiptapFieldSearchableOptions
): Searchable | null {
  const { id, name } = options || {};
  // PERF: Use registry only — does NOT subscribe to SearchStateContext,
  // so this component won't re-render when searchState changes.
  const { register, unregister, subscribeToSearchState, getSearchState } = useSearchableRegistry();

  // Keep track of the editor ref to avoid stale closures
  const editorRef = useRef<Editor | null>(editor);
  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  // Store search state in a ref so the plugin can access it
  const searchStateRef = useRef<SearchState>(getSearchState());

  // Track if plugin has been registered
  const pluginRegisteredRef = useRef(false);
  const pluginKeyRef = useRef<PluginKey | null>(null);

  // PERF: Text segment cache — avoids walking PM tree on every search.
  // Keyed by doc identity (ProseMirror creates a new doc object on each transaction).
  const textCacheRef = useRef<{ doc: ProseMirrorNode | null; segments: TextSegment[] }>({ doc: null, segments: [] });

  // Subscribe to search state changes and manage PM decoration plugin lifecycle.
  // This replaces the previous two useEffects that depended on searchState from context.
  // By using a subscription, we avoid 1000+ React re-renders on every search state change.
  useEffect(() => {
    if (!editor || editor.isDestroyed || !id) return;

    // Create plugin key once (reused across register/unregister cycles)
    if (!pluginKeyRef.current) {
      pluginKeyRef.current = new PluginKey(`searchDecoration-${id}`);
    }
    const pluginKey = pluginKeyRef.current;

    // Helper: create and register the PM decoration plugin
    const registerPlugin = () => {
      if (pluginRegisteredRef.current || editor.isDestroyed) return;

      const decorationPlugin = new Plugin({
        key: pluginKey,
        state: {
          init: (_, state) => {
            const { searchTerm, selectedResult } = searchStateRef.current;
            if (!searchTerm) return DecorationSet.empty;
            return createSearchDecorations(state.doc, searchTerm, selectedResult, id);
          },
          apply: (tr, oldSet, _oldState, newState) => {
            if (tr.docChanged || tr.getMeta(pluginKey)) {
              const { searchTerm, selectedResult } = searchStateRef.current;
              if (!searchTerm) return DecorationSet.empty;
              return createSearchDecorations(newState.doc, searchTerm, selectedResult, id);
            }
            return oldSet.map(tr.mapping, tr.doc);
          },
        },
        props: {
          decorations(state) {
            return this.getState(state);
          },
        },
      });

      editor.registerPlugin(decorationPlugin);
      pluginRegisteredRef.current = true;
    };

    // Helper: unregister the PM decoration plugin
    const unregisterPlugin = () => {
      if (!pluginRegisteredRef.current || editor.isDestroyed) return;
      editor.unregisterPlugin(pluginKey);
      pluginRegisteredRef.current = false;
    };

    // Handle initial state (if a search is already active when this field mounts)
    const initialState = getSearchState();
    searchStateRef.current = initialState;
    if (initialState.searchTerm) {
      registerPlugin();
    }

    // Subscribe to future search state changes
    const unsubscribe = subscribeToSearchState((newState: SearchState) => {
      if (editor.isDestroyed) return;

      const prevState = searchStateRef.current;
      searchStateRef.current = newState;

      const hasSearch = !!newState.searchTerm;

      if (hasSearch && !pluginRegisteredRef.current) {
        // Search just activated — create and register the PM plugin.
        // The plugin's init() will create the correct decorations.
        registerPlugin();
      } else if (hasSearch && pluginRegisteredRef.current) {
        // Search state changed while plugin is active — dispatch to update decorations.
        // Skip if nothing relevant changed.
        const prevSelKey = prevState.selectedResult
          ? `${prevState.selectedResult.sourceId}:${prevState.selectedResult.from}`
          : "";
        const newSelKey = newState.selectedResult
          ? `${newState.selectedResult.sourceId}:${newState.selectedResult.from}`
          : "";
        if (prevState.searchTerm !== newState.searchTerm || prevSelKey !== newSelKey) {
          const tr = editor.view.state.tr.setMeta(pluginKey, true);
          editor.view.dispatch(tr);
        }
      } else if (!hasSearch && pluginRegisteredRef.current) {
        // Search cleared — unregister the PM plugin to free resources
        unregisterPlugin();
      }
    });

    return () => {
      unsubscribe();
      unregisterPlugin();
    };
  }, [editor, id, subscribeToSearchState, getSearchState]);

  const searchable = useMemo<Searchable | null>(() => {
    // Don't create searchable if no id is provided or editor doesn't exist
    if (!id || !editor) return null;

    // Helper: get cached text segments, extracting only when doc changes
    const getCachedSegments = (doc: ProseMirrorNode): TextSegment[] => {
      if (textCacheRef.current.doc === doc) {
        return textCacheRef.current.segments;
      }
      const segments = extractTextSegments(doc);
      textCacheRef.current = { doc, segments };
      return segments;
    };

    return {
      id,
      name: name || id,

      search: (term: string): SearchResult[] => {
        const currentEditor = editorRef.current;
        if (!currentEditor || currentEditor.isDestroyed) return [];

        return searchTiptapDoc(currentEditor.state.doc, term, id, name || id);
      },

      searchWithRegex: (regex: RegExp): SearchResult[] => {
        const currentEditor = editorRef.current;
        if (!currentEditor || currentEditor.isDestroyed) return [];

        const segments = getCachedSegments(currentEditor.state.doc);
        return searchTextSegments(segments, regex, id, name || id);
      },

      replace: (result: SearchResult, replacement: string) => {
        const currentEditor = editorRef.current;
        if (!currentEditor || currentEditor.isDestroyed) return;

        currentEditor.chain()
          .insertContentAt({ from: result.from, to: result.to }, replacement)
          .run();
      },

      replaceAll: (term: string, replacement: string) => {
        const currentEditor = editorRef.current;
        if (!currentEditor || currentEditor.isDestroyed) return;
        if (!currentEditor.view || !currentEditor.state) return;

        const positions = collectTiptapMatchPositions(currentEditor.state.doc, term);
        if (!positions.length) return;

        // Use TipTap's command system to ensure onUpdate is triggered properly
        // Positions are already sorted descending, so replacements maintain valid positions
        currentEditor
          .chain()
          .command(({ tr }) => {
            for (const { from, to } of positions) {
              tr.insertText(replacement, from, to);
            }
            return true;
          })
          .run();
      },

      scrollToResult: (result: SearchResult) => {
        const currentEditor = editorRef.current;
        if (!currentEditor || currentEditor.isDestroyed) return;

        try {
          // Set selection to highlight the result
          currentEditor.chain().setTextSelection({ from: result.from, to: result.to }).run();

          const editorElement = currentEditor.view.dom as HTMLElement;
          const scrollContainer = findScrollContainer(editorElement);

          // Calculate the match's offset within the editor element
          // We use coordsAtPos and subtract the element's top to get element-relative position
          const coords = currentEditor.view.coordsAtPos(result.from);
          const elementRect = editorElement.getBoundingClientRect();

          // The offset within the element is the difference between match coords and element top
          const offsetWithinElement = coords ? coords.top - elementRect.top : 0;

          // Scroll to center the match in the viewport
          scrollToCenterElement(editorElement, offsetWithinElement, scrollContainer);
        } catch (e) {
          Sentry.captureException(e, { tags: { feature: "find_replace_scroll", field: name } });
        }
      },

      getElement: (): HTMLElement | null => {
        const currentEditor = editorRef.current;
        if (!currentEditor || currentEditor.isDestroyed) return null;
        return currentEditor.view.dom as HTMLElement;
      },
    };
  }, [id, name, editor]);

  useEffect(() => {
    if (searchable && editor && !editor.isDestroyed) {
      register(searchable);
      return () => unregister(id!);
    }
  }, [searchable, register, unregister, id, editor]);

  return searchable;
}

export default useTiptapFieldSearchable;
